import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Typography } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import Bar from './Bar';
import { StatementType, LabelType } from './types';
import { LocaleType, getTranslation } from '../utils/getTranslations';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.5rem;
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SeparatorLine = styled.div`
  width: 100%;
  height: 2px;
  background-color: #e3e5e8;
  margin: 2.5rem 0;
`;

const UntouchedKeys = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

interface Props {
  data: StatementType[];
  locale: LocaleType;
  customZeroAnswersLabel?: string | LabelType;
  showZeroBars?: boolean;
}

interface PreparedDataType {
  used: StatementType[];
  dontKnow?: StatementType;
  untouched: (string | LabelType)[];
}

const Statement = ({
  data,
  locale,
  customZeroAnswersLabel,
  showZeroBars,
}: Props) => {
  const t = useFormatMessage();
  const [chartData, setChartData] = useState<PreparedDataType>({
    used: [],
    dontKnow: undefined,
    untouched: [],
  });

  useEffect(() => {
    const preparedData: PreparedDataType = {
      used: [],
      dontKnow: undefined,
      untouched: [],
    };

    data.forEach((singleData) => {
      const isZeroValue = singleData.value === 0;

      if (!isZeroValue) {
        if (singleData.has_none) {
          preparedData.dontKnow = singleData;
        } else {
          preparedData.used.push(singleData);
        }
      } else if (isZeroValue) {
        preparedData.untouched.push(singleData.label);
      }
    });

    setChartData(preparedData);
  }, [data]);

  const areUntouchedKeys = chartData.untouched.length > 0;

  return (
    <Container>
      <Content>
        {chartData.used.map(({ value, label, has_none }, index) => (
          <Bar
            key={index}
            value={value}
            label={label}
            has_none={has_none}
            locale={locale}
          />
        ))}

        {chartData.dontKnow && (
          <Bar
            value={chartData.dontKnow.value}
            label={chartData.dontKnow.label}
            has_none={chartData.dontKnow.has_none}
            locale={locale}
          />
        )}

        {areUntouchedKeys && (
          <>
            {!showZeroBars && (
              <>
                <SeparatorLine />
                <TextWrapper>
                  <Typography fontWeight="500" color="#454A54" mr="0.5rem">
                    {customZeroAnswersLabel
                      ? getTranslation(customZeroAnswersLabel, locale)
                      : t(
                          'reports.statement.info',
                          'Statements that are not associated with the idea'
                        )}
                  </Typography>
                </TextWrapper>
              </>
            )}
            {showZeroBars ? (
              <>
                {chartData.untouched.map((label, index) => (
                  <Bar key={index} value={0} label={label} locale={locale} />
                ))}
              </>
            ) : (
              <UntouchedKeys>
                {chartData.untouched.map((dataKey, index) => {
                  const isLastElem = index === chartData.untouched.length - 1;

                  return (
                    <Typography
                      key={`keys-${index}`}
                      fontWeight="400"
                      color="#454A54"
                      fontSize="0.875rem"
                    >
                      {`${getTranslation(dataKey, locale)}${
                        isLastElem ? '' : ','
                      }`}
                    </Typography>
                  );
                })}
              </UntouchedKeys>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default Statement;
